import React, { FC, useCallback, useMemo, useState } from 'react';
import { Checkbox, useScopedSelector, AttachFileIcon, KEY_CODES } from 'ia-react-core';
import { useDispatch } from 'react-redux';
import { Time, redirectTo } from 'omni-package-react-core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Message } from '../../../../../../interfaces/Message';
import { SecuredMessagingState } from '../../../../../../interfaces/SecuredMessagingState';
import { selectOneMessage } from '../../../../SecuredMessagingPage.actions';
import { getTabName } from '../../../../utils/securedMessagingHelper';
import {
  StyledMessagesTableCellWithoutText,
  StyledTableCellLeft,
  StyledTableCellRight,
  StyledTableRow,
  StyledTableCellIcon,
  StyledMessageValue,
} from './MessagesRow.styles';
import Constants from '../../../../../../Constants';

export interface MessagesRowsProps {
  message: Message;
}

const MessagesRows: FC<MessagesRowsProps> = ({ message }) => {
  const [isFocused, setIsFocused] = useState(false);

  const { t } = useTranslation('SecureMessagingPage');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedMessages: number[] = useScopedSelector((state: SecuredMessagingState) => state.selectedMessages);
  const attachmentsCount = useMemo(() => message.attachments?.length, [message.attachments?.length]);
  const activeTab: number = useScopedSelector((state: SecuredMessagingState) => state.messagesTabs?.selectedTab);

  const isSent = useMemo(() => activeTab === 1, [activeTab]);
  const messageSource = useMemo(() => isSent ? 'Outbox' : 'Inbox', [isSent]);

  const handleCheckBoxChange = useCallback(() => {
    dispatch(selectOneMessage(message.id));
  }, [dispatch, message.id]);
  const messageIsChecked = useMemo(() => selectedMessages?.includes(message.id), [message.id, selectedMessages]);

  const params = new URLSearchParams(window.location.search);
  params.set('messageSource', getTabName(activeTab));
  params.set('page', params.get(`messagesResource${messageSource}`) ? params.get(`messagesResource${messageSource}`) : '1');
  const newSearch = params.toString();

  const onClick = () => {
    redirectTo(`${Constants.MessageDetailsPagePath}/${message.id}?${newSearch}`, navigate);
  };

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isFocused) {
      if (event?.keyCode === KEY_CODES.ENTER || event?.keyCode === KEY_CODES.SPACE) {
        redirectTo(`${Constants.MessageDetailsPagePath}/${message.id}?${newSearch}`, navigate);
      }
    } else if ((event?.keyCode === KEY_CODES.ENTER)) {
      handleCheckBoxChange();
    }
  }, [handleCheckBoxChange, navigate, isFocused, message.id, newSearch]);

  return (
    <StyledTableRow onKeyDown={handleKeyDown} tabIndex={0} unread={message.isNew} aria-label={t('messageRowAriaLabel')}>
      <StyledMessagesTableCellWithoutText>
        <Checkbox
          aria-label={t('labelCheckboxSelect')}
          type="checkbox"
          onChange={handleCheckBoxChange}
          onBlur={() => { setIsFocused(false); }}
          onFocus={() => { setIsFocused(true); }}
          checked={messageIsChecked}
          value=""
          tabIndex={0}
        />
      </StyledMessagesTableCellWithoutText>
      <StyledTableCellLeft onClick={onClick}>
        <StyledMessageValue>{(message.subjectGroupDescription)}</StyledMessageValue>
      </StyledTableCellLeft>
      <StyledTableCellLeft onClick={onClick}>
        <StyledMessageValue>{(message.subjectDescription)}</StyledMessageValue>
      </StyledTableCellLeft>
      <StyledTableCellIcon onClick={onClick}>
        {(attachmentsCount) > 0 && (<AttachFileIcon />)}
      </StyledTableCellIcon>
      <StyledTableCellRight p-t={0} p-b={0} onClick={onClick}>
        <StyledMessageValue><Time time={new Date(message.activationDateUTC)} /></StyledMessageValue>
      </StyledTableCellRight>
    </StyledTableRow>
  );
};

export default MessagesRows;
